import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminView,
  AdminAssessmentStorage,
  RespondentStoreProvider,
  useBundledContent,
  useContentStore,
  useUserStore,
  createcontentService,
  PhiCollectionForm,
  type PhiCollectionFormValues,
  type ApiService,
} from '@pro-groups/shared';
import { AdminStateStorage } from '@pro-groups/shared/services/AdminStateStorage';
import type { AdminStateDto, AdminPreFillDto, AdminDataDto } from '@pro-groups/shared/types/adminState';
import { ReportView } from '@pro-groups/shared/components/views/ReportView';
import type { AdminAssessmentDto } from '@pro-groups/shared/types/adminAssessment';
import type { AdminResponseDto } from '@pro-groups/shared/types/AdminResponseDto';
import { PHI_FIELD, getPhiValue } from '@pro-groups/shared/types/api/patinfo';
import type { PatinfoPhiDto } from '@pro-groups/shared/types/api/patinfo';
import type { AppProfilePHIDto, MVarDataDto } from '@pro-groups/shared/types/api/appProfile';
import type { AdminUVarDto } from '@pro-groups/shared/types/api/service';
import type {
  TokenAssessmentMode,
  SecureLoginResponseDto,
  SecureLoginPreloadDto,
  SecureLoginSettingDto,
  SecureLoginUVarDto,
} from '@pro-groups/shared/types/api/tokenAssessment';
import { webStorageAdapter } from '../storage/WebStorageAdapter';
import { TOKEN_ACCESS_TYPE } from '../auth/authEntry';

interface Props {
  apiService: ApiService;
  mode: TokenAssessmentMode;
  token: string;
}

type ResolvedSession = SecureLoginResponseDto;

/**
 * Link-based sessions (/secure, /remote) always end by redirecting the respondent's
 * browser — there is no in-app "thank you" or report screen for this flow (unlike the
 * SRA-authenticated WellVisitView, which returns to the child profile). completeUrl/
 * cancelUrl are set by whoever created the launch; this is only the fallback when
 * either is blank.
 */
const DEFAULT_EXIT_URL = 'https://www.cnn.com';

/**
 * How long the completion redirect waits for AdminView's background admin_response_submit to
 * acknowledge (the "callback handshake") before navigating away anyway. Long enough for a slow
 * round-trip; short enough that an unreachable backend doesn't strand the respondent on the
 * "Finishing up" screen. The submission itself still persists locally for background retry.
 */
const SUBMIT_HANDSHAKE_TIMEOUT_MS = 15000;

/**
 * completeUrl/cancelUrl are entered by whoever set up the launch and often come in as a bare
 * domain (e.g. "www.psychscan.com") with no scheme. window.location.assign treats a schemeless
 * value as a path RELATIVE to the current page — e.g. "www.psychscan.com" resolves to
 * "http://localhost:3000/secure/www.psychscan.com" instead of actually navigating there.
 * Prepend http:// when no scheme is present so the redirect always targets an absolute URL.
 */
function ensureUrlHasScheme(url: string): string {
  const trimmed = url.trim();
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
}

/** LaunchSecureDto has no dedicated profile-identity field — it travels in `phi` (see ClientLaunchBuilder.ts). */
function resolveProfileId(phi: PatinfoPhiDto[]): string {
  return getPhiValue(phi ?? [], 'patcode');
}

/** resolved.adminState is a JSON-stringified AdminStateDto snapshot (empty string when there's nothing to resume). */
function parseResolvedAdminState(adminState: string): AdminStateDto | null {
  if (!adminState) {
    return null;
  }
  try {
    return JSON.parse(adminState) as AdminStateDto;
  } catch (err) {
    console.warn('SecureAdministrationView: failed to parse resolved.adminState', err);
    return null;
  }
}

/**
 * SecureLoginResponseDto.preloads/extra are expected camelCase (AuthenticationController
 * goes through ASP.NET's recursive camelCase policy — confirmed, not just inferred). If the
 * backend serializer config changes or this endpoint's response bypasses that policy, these
 * arrays would arrive PascalCase instead and every prefill/data value would silently read as
 * empty. Flag it loudly rather than fail silent, since the backend is still under active
 * development.
 */
function warnIfPascalCaseLeak(items: readonly unknown[], camelKeys: string[], context: string): void {
  const first = items[0] as Record<string, unknown> | undefined;
  if (!first) {
    return;
  }
  const hasCamel = camelKeys.some((k) => k in first);
  if (!hasCamel) {
    console.warn(
      `SecureAdministrationView: ${context} does not look camelCase (expected keys: ${camelKeys.join(', ')}, got: ${Object.keys(first).join(', ')}). ` +
      `Check AuthenticationController's JSON serializer policy for the secure-login response.`
    );
  }
}

function buildPhiFromResolved(phi: PatinfoPhiDto[]): AppProfilePHIDto {
  const fname = getPhiValue(phi, PHI_FIELD.FNAME);
  const lname = getPhiValue(phi, PHI_FIELD.LNAME);
  return {
    dname: [fname, lname].filter(Boolean).join(' '),
    fname,
    lname,
    email: getPhiValue(phi, PHI_FIELD.EMAIL),
    dob: getPhiValue(phi, PHI_FIELD.DOB),
    gender: getPhiValue(phi, PHI_FIELD.GENDER),
    extra: [],
  };
}

/**
 * Converts SecureLoginResponseDto.uVars (real camelCase wire shape) into the PascalCase
 * AdminUVarDto[] shape AdminStateDto.uVars carries locally — matching what account-local
 * resolution (AdminStateStorage.resolveAccountUVars) already produces from AccountPlaDto.UVars,
 * and what gets sent back to the backend in AdminSubmitDto.UVars. Mirrors buildPhiFromResolved
 * above for the parallel phi flow.
 */
function buildUVarsFromResolved(uVars: SecureLoginUVarDto[]): AdminUVarDto[] {
  // Read each field in either casing (camelCase per the contract, PascalCase if the response
  // ever bypasses the serializer's naming policy — see warnIfPascalCaseLeak) so a casing drift
  // can't silently produce entries with an undefined UvarTypeId that never match a question.
  return uVars.map((raw) => {
    const u = raw as any;
    return {
      UvarTypeId: u.uvarTypeId ?? u.UvarTypeId,
      DName: u.dName ?? u.DName,
      MvarName: u.mvarName ?? u.MvarName,
      HtmlUvarAns: ((u.htmlUvarAns ?? u.HtmlUvarAns ?? []) as any[]).map((a) => ({
        AText: a.aText ?? a.AText,
        LanguageTypeId: a.languageTypeId ?? a.LanguageTypeId,
        KeyValue: a.keyValue ?? a.KeyValue,
        DataValue: a.dataValue ?? a.DataValue,
      })),
    };
  });
}

/**
 * Entry point rendered by App.tsx for a matched /secure, /remote, /link, or /share token
 * route (see authEntry.ts's resolveTokenRoute). Resolves the token into a
 * scoped administration session (Phase A), then mounts the real assessment
 * engine (AdminView/ReportView, Phase B) under a RespondentStoreProvider —
 * replacing the old WebTokenAssessmentView stub, which never rendered real
 * content or submitted real answers.
 *
 * A secure-login/session error is shown in place on this same page (StatusScreen) rather
 * than navigating anywhere — this link never involved an SRA sign-in, so there is no
 * meaningful "exit" destination to send the respondent to.
 */
export const SecureAdministrationView: React.FC<Props> = ({ apiService, mode, token }) => {
  const [resolved, setResolved] = useState<ResolvedSession | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // secure-login mints a real session server-side, so it's not safe to fire twice —
  // React.StrictMode (enabled in main.tsx) intentionally double-invokes effects in dev
  // (mount → cleanup → mount) to surface exactly this kind of non-idempotent side effect.
  // Cache the in-flight PROMISE (not just a "did we start" flag) keyed by token/mode, so
  // every invocation — including the one that didn't start the request — attaches its own
  // cancellation-aware handler to the SAME promise. A flag-only guard breaks this: the first
  // invocation's own cleanup marks IT cancelled before the request resolves, and if the
  // second invocation skips starting a new request without also awaiting the first one's
  // result, nothing ever calls setResolved and the view hangs on "Loading" forever.
  const resolveRequestRef = useRef<{ key: string; promise: Promise<Awaited<ReturnType<ApiService['resolveSecureAdministration']>>> } | null>(null);

  useEffect(() => {
    const requestKey = `${mode}:${token}`;
    if (resolveRequestRef.current?.key !== requestKey) {
      resolveRequestRef.current = { key: requestKey, promise: apiService.resolveSecureAdministration(mode, token) };
    }
    const { promise } = resolveRequestRef.current;

    let cancelled = false;
    setResolveError(null);
    void promise.then((result) => {
      if (cancelled) {
        return;
      }
      if (!result.success || !result.data) {
        setResolveError(result.message || 'Unable to resolve this administration session.');
        return;
      }
      setResolved(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [apiService, mode, token]);

  if (resolveError) {
    return (
      <StatusScreen title="Session Error" message={resolveError} />
    );
  }

  if (!resolved) {
    return (
      <StatusScreen title="Loading" message="Validating your assessment link…" />
    );
  }

  return (
    <RespondentStoreProvider storage={webStorageAdapter} resolved={resolved}>
      <SecureAdministrationInner
        apiService={apiService}
        resolved={resolved}
        mode={mode}
        token={token}
      />
    </RespondentStoreProvider>
  );
};

const SecureAdministrationInner: React.FC<{
  apiService: ApiService;
  resolved: ResolvedSession;
  mode: TokenAssessmentMode;
  token: string;
}> = ({ apiService, resolved, mode, token }) => {
  // Backend contract sanity check — surfaces loudly rather than failing silent, since the
  // backend is still under active development (mirrors warnIfPascalCaseLeak's approach above).
  useEffect(() => {
    const expected = TOKEN_ACCESS_TYPE[mode];
    if (resolved.accessType && resolved.accessType !== expected) {
      console.warn(
        `SecureAdministrationView: resolved.accessType ("${resolved.accessType}") does not match the expected AccessType for mode "${mode}" ("${expected}").`
      );
    }
  }, [resolved.accessType, mode]);

  const contentStoreHook = useContentStore();
  const stableContentStore = useMemo(() => contentStoreHook, [contentStoreHook]);
  const { status: bundleStatus } = useBundledContent(webStorageAdapter, stableContentStore, true);
  const userStoreHook = useUserStore();

  // Backend fallback for content not present in the local bundle — mirrors
  // ChildNav.tsx's construction of the same service for the authenticated flow.
  const contentService = useMemo(
    () => (contentStoreHook && apiService ? createcontentService(apiService, contentStoreHook.getState()) : null),
    [contentStoreHook, apiService]
  );

  const [lookupError, setLookupError] = useState<string | null>(null);
  const [currentAdminState, setCurrentAdminState] = useState<AdminStateDto | null>(null);
  const [currentSegmentId, setCurrentSegmentId] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'phi-collection' | 'administering' | 'report' | 'error'>('loading');
  const [wasResumed, setWasResumed] = useState(false);
  // Resolved once in the init effect below — kept in state (not just a local variable there)
  // so handleComplete/the 'report' phase render can still reach it after that effect has run.
  const [assessmentDefinition, setAssessmentDefinition] = useState<AdminAssessmentDto | null>(null);
  // Set by handleComplete when the closing-segment PreScript called renderReport(reportId) —
  // drives the 'report' phase render below. selectedReportId/adminResponse travel together so
  // there's no window where one is set without the other.
  const [pendingReport, setPendingReport] = useState<{ selectedReportId: string; adminResponse: AdminResponseDto } | null>(null);

  // Backend-submission handshake. AdminView starts admin_response_submit in the background the
  // moment the respondent taps the closing "Done" button, then calls onBackendSubmitSettled
  // once it lands. The completion redirect (window.location.assign — which aborts any in-flight
  // fetch) must not fire until then, so it awaits `submitHandshakeRef.current.promise`.
  // `submitPhase` drives the brief "Finishing up" screen shown while that wait is in progress.
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'finishing'>('idle');
  const submitHandshakeRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  const getSubmitHandshake = useCallback(() => {
    if (!submitHandshakeRef.current) {
      let resolve!: () => void;
      const promise = new Promise<void>((res) => { resolve = res; });
      submitHandshakeRef.current = { promise, resolve };
    }
    return submitHandshakeRef.current;
  }, []);

  const profilePHI = useMemo(() => buildPhiFromResolved(resolved.phi ?? []), [resolved.phi]);
  const profileUVars = useMemo(() => buildUVarsFromResolved(resolved.uVars ?? []), [resolved.uVars]);
  // A blank/absent uVars means every uvar question shows its full content answer set instead of
  // the account's restricted one — flag it, since that's a backend contract gap (secure-login
  // not returning LaunchSecureDto/AccountPlaDto UVars), not something the client can repair.
  useEffect(() => {
    if (!resolved.uVars || resolved.uVars.length === 0) {
      console.warn(
        'SecureAdministrationView: secure-login response has no uVars — uvar questions (e.g. phigender) ' +
        'will show their full answer set. Check the launch/PLA was saved with UVars and the backend returns them.',
        { hasField: resolved.uVars !== undefined }
      );
    }
  }, [resolved.uVars]);
  // /secure and /remote sessions are tied to an existing client profile, so resolved.phi
  // always carries a real patcode. /link sessions usually aren't — the link isn't bound to
  // any specific client, so patcode comes back blank. Falling through to the empty string
  // here breaks two things: AdminStateStorage keys every session as `admin_state__{sourceId}`
  // (no profileId segment), so *every* /link respondent for the same assessment collides on
  // the same local storage entry; and AdminView's goToNextPage treats a falsy profileId as
  // missing required data and bails silently — including its very first PreScript-evaluation
  // call, which is why the question text never gets substituted and the page looks frozen.
  // Falling back to the session's own unique id keeps every /link launch isolated locally;
  // the backend is expected to create/assign the real patient record from the submitted PHI
  // and return the authoritative identity via AdminSubmitResponseDto.RecPatient/ID.
  // resolved.sessionId is a backend-synthesized value (type comment: "Synthetic session
  // identity, e.g. secure:{SSKey}") — confirmed via reproduction that it can come back blank
  // for /link sessions specifically (a backend gap, still under active development), which
  // silently reproduced the exact bug this whole fallback chain exists to prevent. `token`
  // (the URL token that reached this page) is available before any backend round-trip, so it
  // can never be blank — use it as the final, always-reliable fallback.
  const profileId = useMemo(
    () => resolveProfileId(resolved.phi ?? []) || resolved.sessionId || token,
    [resolved.phi, resolved.sessionId, token]
  );

  // Once bundled content has loaded, resolve the assessment definition and
  // stand up (or resume) the AdminStateDto driving this administration.
  useEffect(() => {
    if (!bundleStatus.isLoaded || currentAdminState || phase !== 'loading') {
      return;
    }

    let cancelled = false;
    void (async () => {
      const assessmentStorage = new AdminAssessmentStorage(webStorageAdapter);
      let definition = await assessmentStorage.getAssessmentBySourceId(resolved.assessmentId);
      if (!definition) {
        // Fall back to a surveyId match in case assessmentId maps to that field instead.
        const all = await assessmentStorage.getAvailableAssessments();
        definition = all.find((a) => a.surveyId === resolved.assessmentId) ?? null;
      }

      if (cancelled) {
        return;
      }

      if (!definition) {
        setLookupError(`Assessment content for "${resolved.dName || resolved.assessmentId}" was not found.`);
        setPhase('error');
        return;
      }

      setAssessmentDefinition(definition);

      const adminStateStorage = new AdminStateStorage(webStorageAdapter);
      const resumedAdminState = parseResolvedAdminState(resolved.adminState);
      let adminState: AdminStateDto;
      if (resumedAdminState) {
        // Server-side resume (Phase A2) — a previously-suspended session.
        adminState = resumedAdminState;
        // Defensive backfill: older suspended snapshots predate the skinType/accessType/
        // launchId/adminRequestId fields being persisted at creation time.
        adminState.accessType = adminState.accessType || resolved.accessType;
        adminState.skinType = adminState.skinType || resolved.skinType;
        adminState.launchId = adminState.launchId || resolved.launchId;
        adminState.adminRequestId = adminState.adminRequestId || resolved.adminRequestId;
        // Refresh account-level PLA preloads (surveyblist, adminreportid, etc.) on resume —
        // don't leave a resumed session stuck with whatever was cached (or missing) when it was
        // originally suspended if Account > Assessment Options has changed since.
        adminState = await adminStateStorage.refreshAccountPreloads(adminState, resolved.accountId);
        setWasResumed(true);
      } else {
        adminState = await adminStateStorage.createAdminState(
          resolved.accountId,
          profileId,
          definition.surveyId,
          definition.sourceId,
          definition.surveybList,
          '1',
          definition.dName,
          undefined,
          profilePHI,
          profileUVars
        );
        // Persisted so accessType/skinType/launchId/adminRequestId survive a suspend/resume
        // round-trip — AdminView's own extraSubmitFields only stamps accessType transiently
        // at submit time.
        adminState.accessType = resolved.accessType;
        adminState.skinType = resolved.skinType;
        // Carried straight onto the state as first-class fields (not a data[] entry, unlike
        // the old LaunchToken data push this replaces) — flows into
        // AdminSubmitDto.LaunchId/.AdminRequestId when admin_response_submit sends this state
        // (see AdminView's saveAdminResultToBackend, which spreads adminState verbatim into
        // the payload). Saved unconditionally below so it's persisted even when there's no
        // prefill/data to seed.
        adminState.launchId = resolved.launchId;
        adminState.adminRequestId = resolved.adminRequestId;
        await adminStateStorage.saveAdminState(adminState);

        // Seed prefill/data from the launch's Preloads/Extra, matching the
        // pre-population pattern WellVisitView.tsx uses for its own snapshot flow.
        const resolvedPreloads: SecureLoginPreloadDto[] = resolved.preloads ?? [];
        const resolvedExtra: SecureLoginSettingDto[] = resolved.extra ?? [];
        warnIfPascalCaseLeak(resolvedPreloads, ['fieldCode', 'value'], 'SecureLoginResponseDto.preloads');
        warnIfPascalCaseLeak(resolvedExtra, ['name', 'value'], 'SecureLoginResponseDto.extra');

        const prefill: AdminPreFillDto[] = resolvedPreloads.map((p) => ({
          name: p.fieldCode,
          value: p.value,
        }));
        // resolved.phi already becomes the first-class adminState.phi (AppProfilePHIDto, via
        // profilePHI above), but PreScript's getData()/putData() only ever see the flat data[]
        // array — and the backend's admin submission handling searches THAT array for PHI
        // updates during administration rather than treating AdminSubmitDto.Phi as the sole
        // source. Mirror every SecureLoginResponseDto.Phi entry into data[] too (same name/value
        // shape as PatinfoPhiDto) so a page script can read/update PHI like any other data[]
        // entry and the backend picks up the change the same way.
        const phiData: AdminDataDto[] = (resolved.phi ?? []).map((p) => ({
          name: p.name,
          value: p.value,
          segment: '',
          pageNum: '',
        }));
        const data: AdminDataDto[] = [
          ...phiData,
          ...resolvedExtra.map((e) => ({
            name: e.name,
            value: e.value,
            segment: '',
            pageNum: '',
          })),
        ];
        if (prefill.length || data.length) {
          adminState.prefill = [...(adminState.prefill ?? []), ...prefill];
          adminState.data = [...(adminState.data ?? []), ...data];
          await adminStateStorage.saveAdminState(adminState);
        }
      }

      if (cancelled) {
        return;
      }

      setCurrentAdminState(adminState);
      setCurrentSegmentId(adminState.segment || definition.surveybList.split(',')[0]);
      // /link sessions gather respondent PHI (fname/lname/dob/gender/email) before
      // administration starts — resumed sessions already have it, so skip straight through.
      setPhase(mode === 'link' && !resumedAdminState ? 'phi-collection' : 'administering');
    })();

    return () => {
      cancelled = true;
    };
  }, [bundleStatus.isLoaded, currentAdminState, phase, resolved, profilePHI, profileUVars, profileId, mode]);

  // /link (AccessType 41) only: respondent-entered PHI, collected before administration
  // starts, merges into the AdminStateDto created above and is persisted immediately so
  // it survives a suspend/resume round-trip. `values` is keyed by PHI_FIELD name — fname/
  // lname/dob/gender/email map to AppProfilePHIDto's own fields, anything else (phone,
  // address, patientid, etc. — whatever the link's launch configuration asked for) goes
  // into `extra`.
  const handlePhiCollected = useCallback((values: PhiCollectionFormValues) => {
    setCurrentAdminState((prev) => {
      if (!prev) {
        return prev;
      }
      const fname = values[PHI_FIELD.FNAME] ?? prev.phi?.fname ?? '';
      const lname = values[PHI_FIELD.LNAME] ?? prev.phi?.lname ?? '';
      const knownFields = new Set<string>([PHI_FIELD.FNAME, PHI_FIELD.LNAME, PHI_FIELD.DOB, PHI_FIELD.GENDER, PHI_FIELD.EMAIL]);
      const extra: MVarDataDto[] = Object.entries(values)
        .filter(([name]) => !knownFields.has(name))
        .map(([name, value]) => ({ name, value }));
      const mergedPhi: AppProfilePHIDto = {
        dname: [fname, lname].filter(Boolean).join(' '),
        fname,
        lname,
        dob: values[PHI_FIELD.DOB] ?? prev.phi?.dob ?? '',
        gender: values[PHI_FIELD.GENDER] ?? prev.phi?.gender ?? '',
        email: values[PHI_FIELD.EMAIL] ?? prev.phi?.email ?? '',
        extra,
      };
      const nextAdminState: AdminStateDto = { ...prev, phi: mergedPhi };
      void new AdminStateStorage(webStorageAdapter).saveAdminState(nextAdminState);
      return nextAdminState;
    });
    setPhase('administering');
  }, []);

  const redirectToCompleteUrl = useCallback(() => {
    const target = ensureUrlHasScheme(resolved.completeUrl || DEFAULT_EXIT_URL);
    // Diagnostic: if this ever lands back on this app's own /secure route (re-triggering
    // resolveSecureAdministration with a now-invalid token), the value logged here is the
    // one to check against what the launch/backend actually set as CompleteUrl.
    console.log('SecureAdministrationView: redirecting on completion', { completeUrl: resolved.completeUrl, target });
    window.location.assign(target);
  }, [resolved.completeUrl]);

  // AdminView's onBackendSubmitSettled — the background admin_response_submit has landed
  // (or was skipped/failed). Release the redirect handshake either way; the local record is
  // kept for retry on failure, so there's nothing to gain by blocking navigation further.
  const handleBackendSubmitSettled = useCallback((result: { submitted: boolean }) => {
    console.log('SecureAdministrationView: backend submit settled', result);
    getSubmitHandshake().resolve();
  }, [getSubmitHandshake]);

  // The completion redirect, gated on the backend-submission handshake. Shows the "Finishing
  // up" screen, waits for onBackendSubmitSettled (capped by SUBMIT_HANDSHAKE_TIMEOUT_MS so an
  // unreachable backend can't strand the respondent), then navigates away.
  const redirectAfterSubmitHandshake = useCallback(async () => {
    setSubmitPhase('finishing');
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      getSubmitHandshake().promise,
      new Promise<void>((res) => { timeoutId = setTimeout(res, SUBMIT_HANDSHAKE_TIMEOUT_MS); }),
    ]);
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    redirectToCompleteUrl();
  }, [getSubmitHandshake, redirectToCompleteUrl]);

  // Link-based sessions redirect on completion by default — there is no in-app report/
  // thank-you screen otherwise (contrast WellVisitView, which returns to the child profile).
  // completeUrl is set by whoever created the launch. If the closing-segment PreScript called
  // renderReport(reportId), though, show that report first (immediate feedback for the
  // respondent before they see the provider) — the redirect still happens, but only once the
  // respondent taps Continue on the report screen (see the 'report' phase render below).
  // Either way the redirect waits on the backend-submission handshake (redirectAfterSubmitHandshake).
  const handleComplete = useCallback((finalState: AdminStateDto, localAdminResponse?: AdminResponseDto) => {
    const pendingReportId = (finalState as any).renderReport as string | undefined;
    if (pendingReportId && localAdminResponse) {
      setCurrentAdminState(null);
      setPendingReport({ selectedReportId: pendingReportId, adminResponse: localAdminResponse });
      setPhase('report');
      return;
    }
    setCurrentAdminState(null);
    void redirectAfterSubmitHandshake();
  }, [redirectAfterSubmitHandshake]);

  const handleCancel = useCallback(async () => {
    if (currentAdminState) {
      try {
        // `currentAdminState` here is the state as of assessment START — AdminView owns
        // paging/answers internally and keeps AdminStateStorage's (local storage) copy
        // current on every page advance (see AdminView.tsx's goToNextPage, which
        // re-fetches from AdminStateStorage before every save for the same reason), but
        // never reports changes back up to this component. Re-read the live state from
        // the same source AdminView itself trusts, rather than sending this stale snapshot.
        const adminStateStorage = new AdminStateStorage(webStorageAdapter);
        const latestAdminState = await adminStateStorage.loadAdminState(profileId, currentAdminState.sourceId)
          ?? currentAdminState;

        // Suspend is a data operation, not an authentication concern — it goes
        // through the normal ServicesController RequestType pattern (same
        // processServiceRequestWithRefresh path every other backend call in
        // this app uses), authenticated by the JWT resolveSecureAdministration
        // already applied to this ApiService instance.
        await apiService.processServiceRequestWithRefresh(
          {
            RequestType: 'admin_state_suspend',
            Payload: JSON.stringify(latestAdminState),
          },
          'sync',
          { userStore: userStoreHook, storage: webStorageAdapter }
        );
      } catch (err) {
        // Local save-and-exit (AdminStateStorage) already applies regardless —
        // this is only the best-effort cross-device sync half of suspend.
        console.warn('SecureAdministrationView: failed to push suspend state to backend', err);
      }
    }
    setCurrentAdminState(null);
    window.location.assign(ensureUrlHasScheme(resolved.cancelUrl || DEFAULT_EXIT_URL));
  }, [apiService, currentAdminState, profileId, resolved.cancelUrl, userStoreHook]);

  if (phase === 'error') {
    return <StatusScreen title="Unable to Start" message={lookupError ?? 'Something went wrong.'} />;
  }

  // Shown while the completion redirect waits on the backend-submission handshake — covers
  // both the no-report path (straight from AdminView) and the report path (respondent tapped
  // "Continue"). Takes precedence over every phase below so the in-flight submit finishes
  // before window.location.assign tears the page down.
  if (submitPhase === 'finishing') {
    return <StatusScreen title="Finishing up" message="Submitting your responses…" />;
  }

  if (phase === 'report' && pendingReport) {
    // ReportView renders its own AdminView-styled page (skin header, single question-page-
    // style card, footer exit button) — onBack/footerButtonLabel drive that footer button to
    // proceed to completeUrl instead of "navigate back", since there's nothing to go back to
    // in this flow.
    return (
      <ReportView
        profileId={profileId}
        recId={pendingReport.adminResponse.recId}
        assessmentDefinition={assessmentDefinition ?? undefined}
        storage={webStorageAdapter}
        profilePHI={profilePHI}
        apiService={apiService}
        accountId={resolved.accountId}
        skinType={resolved.skinType}
        initialAdminResponse={pendingReport.adminResponse}
        selectedReportId={pendingReport.selectedReportId}
        isRespondentSession
        onBack={() => { void redirectAfterSubmitHandshake(); }}
        footerButtonLabel="Continue"
      />
    );
  }

  if (phase === 'loading' || !currentAdminState || !currentSegmentId) {
    return <StatusScreen title="Loading" message="Preparing your assessment…" />;
  }

  if (phase === 'phi-collection') {
    return (
      <PhiCollectionForm
        dName={resolved.dName}
        phiFields={resolved.phi ?? []}
        skinType={resolved.skinType}
        storage={webStorageAdapter}
        onSubmit={handlePhiCollected}
      />
    );
  }

  return (
    <AdminView
      accountId={resolved.accountId}
      profileId={profileId}
      assessmentId={currentSegmentId}
      languageId="1"
      skinType={resolved.skinType}
      onComplete={handleComplete}
      onBackendSubmitSettled={handleBackendSubmitSettled}
      onCancel={() => { void handleCancel(); }}
      useContentStore={stableContentStore}
      apiService={apiService}
      contentService={contentService}
      storage={webStorageAdapter}
      skipContentAutoLoad={wasResumed}
      initialAdminState={currentAdminState}
      extraSubmitFields={{
        SessionId: resolved.sessionId,
        AssessmentId: resolved.assessmentId,
        AccessType: resolved.accessType,
      }}
      isRespondentSession
    />
  );
};

const StatusScreen: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#eef2f7' }}>
    <div style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 16, padding: '28px 24px', boxShadow: '0 6px 24px rgba(0,0,0,0.1)' }}>
      <h1 style={{ margin: 0, fontSize: 24, color: '#1f2937' }}>{title}</h1>
      <p style={{ marginTop: 12, color: '#374151', fontSize: 14 }}>{message}</p>
    </div>
  </div>
);
